import { DatePicker, DateRangePicker, Form, InputGroup } from 'rsuite';
import styles from './dateRangePicker.module.scss';
import React, { useEffect } from 'react';
import moment from 'moment';

export type DateRange = {
  label: string;
  dateRange: [Date, Date];
};

export interface RsDateRangePickerProps {
  startDate: string;
  endDate: string;
  minDate?: string;
  maxDate?: string;
  onStartDateChange: (date: any) => void;
  onEndDateChange: (date: any) => void;
  onChange?: (startDate: string, endDate: string) => void;
  shortcuts?: DateRange[];
  showError?: boolean;
  errorMessage?: string;
  startDatePickerDisabled?: boolean;
  endDatePickerDisabled?: boolean;
  showTimePicker?: boolean;
  disabledHour?: boolean;
  disabled?: boolean;
  cleanable?: boolean;
  name?: string;
  hasError?: boolean;
}

export const RsDateRangePicker = ({
  startDate,
  endDate,
  minDate,
  maxDate,
  onStartDateChange,
  onEndDateChange,
  showError = false,
  errorMessage = '',
  startDatePickerDisabled = false,
  endDatePickerDisabled = false,
  showTimePicker = false,
  disabledHour = true,
  disabled = false,
  cleanable = false,
}: RsDateRangePickerProps) => {
  const { combine, after, before } = DateRangePicker;
  // 初始化日期格式
  const formatDateInternal = () => {
    return showTimePicker ? 'yyyy-MM-dd HH:mm' : 'yyyy-MM-dd';
  };
  const formatDateEmitInternal = () => {
    return showTimePicker ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD';
  };
  // 禁用日期，不允许選擇小於minDate或大於endDate的日期
  const shouldDisableStartDate = () => {
    if (minDate) {
      if (maxDate) {
        if (moment().isAfter(moment(minDate), 'day')) {
          return combine(before(new Date()), after(new Date(maxDate)));
        }
        return combine(before(new Date(minDate)), after(new Date(maxDate)));
      }
      return before(new Date(minDate));
    }
    return undefined;
  };

  const shouldDisableEndDate = () => {
    if (minDate) {
      if (maxDate) {
        if (startDate) {
          return combine(
            before(moment(startDate).startOf('day').toDate()),
            after(new Date(maxDate)),
          );
        }
        return combine(before(new Date(minDate)), after(new Date(maxDate)));
      }
      return before(new Date(minDate));
    }
    return undefined;
  };

  const shouldDisableStartHour = () => {
    if (!disabledHour) return undefined;
    return (hour, date) => {
      return (
        moment(date).isSame(moment(new Date()).startOf('day'), 'days') &&
        moment(new Date()).hour() + 1 > hour
      );
    };
  };

  const shouldDisableEndHour = () => {
    if (!disabledHour) return undefined;
    return (hour, date) => {
      if (moment(date).isSame(moment(new Date()).startOf('day'), 'days')) {
        return moment(new Date()).hour() + 1 > hour;
      }
      return (
        moment(date).isSame(moment(startDate).endOf('day'), 'days') &&
        moment(startDate).hour() > hour
      );
    };
  };

  const shouldDisableEndMinute = () => {
    if (!showTimePicker) return undefined;
    return (minute, date) => moment(date).hour() != 0 && minute === 0;
  };

  const handleStartChange = (range) => {
    onStartDateChange(
      moment(range).format(formatDateEmitInternal()) || [null, null],
    );
  };

  const handleEndChange = (range) => {
    const minute = moment(range).minute() === 0 ? 0 : 59;
    onEndDateChange(
      moment(range).seconds(minute).format(formatDateEmitInternal()) || [
        null,
        null,
      ],
    );
  };

  const handleOnClearStartDate = () => {
    onStartDateChange([null, null]);
  };

  const handleOnClearEndDate = () => {
    onEndDateChange([null, null]);
  };

  useEffect(() => {
    // 修正開始日期大於結束日期的情況
    if (startDate && endDate) {
      if (moment(startDate).isAfter(moment(endDate))) {
        onEndDateChange(
          moment(startDate).endOf('day').format(formatDateEmitInternal()),
        );
      }
    }
  }, [startDate, endDate]);

  const dateRef = React.useRef<HTMLDivElement>(null);

  return (
    <div className='relative' ref={dateRef}>
      <InputGroup style={{ width: 424 }}>
        <DatePicker
          container={dateRef.current ?? undefined}
          format={formatDateInternal()}
          defaultValue={startDate ? new Date(startDate) : undefined}
          value={cleanable ? undefined : new Date(startDate)}
          block={false}
          cleanable={cleanable}
          editable={false}
          appearance='subtle'
          style={{ width: 230 }}
          ranges={[
            {
              label: 'today',
              value: moment().add(1, 'hour').minutes(0).toDate(),
            },
          ]}
          shouldDisableDate={shouldDisableStartDate()}
          shouldDisableHour={shouldDisableStartHour()}
          hideMinutes={(minute) => minute !== 0}
          disabled={startDatePickerDisabled || disabled}
          onChange={handleStartChange}
          onClean={handleOnClearStartDate}
        />
        <InputGroup.Addon>~</InputGroup.Addon>
        <DatePicker
          container={dateRef.current ?? undefined}
          format={formatDateInternal()}
          defaultValue={endDate ? new Date(endDate) : undefined}
          value={cleanable ? undefined : new Date(endDate)}
          block={false}
          cleanable={cleanable}
          editable={false}
          appearance='subtle'
          style={{ width: 230 }}
          ranges={[
            {
              label: 'today',
              value: moment().endOf('day').toDate(),
            },
          ]}
          shouldDisableDate={shouldDisableEndDate()}
          shouldDisableHour={shouldDisableEndHour()}
          shouldDisableMinute={shouldDisableEndMinute()}
          hideMinutes={(minute, date) => minute !== 59 && minute !== 0}
          disabled={endDatePickerDisabled || disabled}
          onChange={handleEndChange}
          onClean={handleOnClearEndDate}
        />
      </InputGroup>
      <Form.ErrorMessage show={showError}>{errorMessage}</Form.ErrorMessage>
    </div>
  );
};
